import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, MapPin, Clock, User, ArrowRight,
  XCircle, Loader2, CheckCircle2, Shield, CreditCard,
  FileText, Phone, Mail, IndianRupee, Copy, Check, ExternalLink
} from 'lucide-react';
import { formatINR } from '@/utils/currency';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import KYCModal from '@/components/investor/KYCModal';
import PaymentSuccessScreen from '@/components/investor/PaymentSuccessScreen';

const API = process.env.REACT_APP_BACKEND_URL;
const EOI_AMOUNT = 25000;

export default function InvestmentLinkLanding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError({ type: 'invalid', message: 'No invitation token provided.' });
      setLoading(false);
      return;
    }
    fetch(`${API}/api/i/${token}`)
      .then(res => {
        if (!res.ok) return res.json().then(d => Promise.reject(d));
        return res.json();
      })
      .then(d => setData(d.data || d))
      .catch(err => {
        const detail = typeof err.detail === 'object'
          ? err.detail.message || JSON.stringify(err.detail) : err.detail || err.message || 'Something went wrong';
        const errorCode = typeof err.detail === 'object' ? err.detail.error : '';
        if (errorCode === 'LINK_EXPIRED' || detail.includes('expired'))
          setError({ type: 'expired', message: detail });
        else if (errorCode === 'ALREADY_CONVERTED' || detail.includes('already invested'))
          setError({ type: 'converted', message: detail });
        else setError({ type: 'invalid', message: detail });
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen bg-[#030303] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white/20" /></div>;
  if (error) return <ErrorState error={error} />;
  if (!data) return <ErrorState error={{ type: 'invalid', message: 'Unable to load investment details.' }} />;
  return <InvestmentFlow data={data} linkToken={token} />;
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* ERROR STATES                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

function ErrorState({ error }) {
  const config = {
    expired: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-400/10', title: 'Invitation Expired' },
    converted: { icon: CheckCircle2, color: 'text-[#4ADE80]', bg: 'bg-[#4ADE80]/10', title: 'Already Invested' },
    invalid: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10', title: 'Link Not Found' },
  };
  const c = config[error.type] || config.invalid;
  const Icon = c.icon;
  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4" data-testid={`error-${error.type}`}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
        <div className={`w-16 h-16 ${c.bg} rounded-full flex items-center justify-center mx-auto mb-4`}><Icon className={`w-8 h-8 ${c.color}`} /></div>
        <h1 className="text-xl font-light text-white mb-2">{c.title}</h1>
        <p className="text-sm text-white/40">{error.message}</p>
        {error.type === 'converted' && (
          <Button onClick={() => window.location.href = '/investor'} className="mt-6 bg-[#4ADE80] text-black" data-testid="go-dashboard-btn">
            Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
        <p className="text-xs text-white/20 mt-4">Please contact your broker for assistance.</p>
      </motion.div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* MAIN INVESTMENT FLOW — the core conversion funnel                         */
/* ═══════════════════════════════════════════════════════════════════════════ */

function InvestmentFlow({ data, linkToken }) {
  const navigate = useNavigate();
  const assignment = data.assignment || {};
  const unit = data.unit || {};
  const broker = data.broker || {};

  // Flow state: details | auth | kyc | payment | success
  const [flowStep, setFlowStep] = useState('details');
  const [assignmentStatus, setAssignmentStatus] = useState(assignment.status || 'CREATED');
  const [ctaLoading, setCtaLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showKYC, setShowKYC] = useState(false);

  // Derived
  const ownershipPct = assignment.ownershipPercentage || 0;
  const capitalAmount = (assignment.assignedAmount || 0) - EOI_AMOUNT;
  const expiresAt = assignment.expiresAt ? new Date(assignment.expiresAt) : null;
  const hoursLeft = expiresAt ? Math.max(0, Math.round((expiresAt - new Date()) / 3600000)) : null;

  // On mount: check if user is logged in
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
        if (assignmentStatus === 'CREATED' || assignmentStatus === 'CLICKED') {
          fetch(`${API}/api/i/${linkToken}/update-status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'REGISTERED', investorId: parsed.userId || parsed.id }),
          }).then(res => {
            if (res.ok) {
              setAssignmentStatus('REGISTERED');
              toast.success('Linked! Now complete your KYC.');
              setShowKYC(true);
            }
          }).catch(() => {});
        } else if (assignmentStatus === 'EOI_PAID' || assignmentStatus === 'CONVERTED') {
          setFlowStep('success');
        } else if (assignmentStatus === 'KYC_DONE') {
          setFlowStep('payment');
        }
      } catch { /* invalid stored user */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── CTA CLICK HANDLER ─────────────────────────────────────────────
  const handleCTA = useCallback(async () => {
    if (!user) {
      localStorage.setItem('investmentReturnTo', `/i/${linkToken}`);
      window.location.href = '/auth';
      return;
    }

    if (assignmentStatus === 'CREATED' || assignmentStatus === 'CLICKED') {
      setCtaLoading(true);
      try {
        const res = await fetch(`${API}/api/i/${linkToken}/update-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'REGISTERED', investorId: user.userId || user.id }),
        });
        if (!res.ok) throw new Error('Failed to register');
        setAssignmentStatus('REGISTERED');
        toast.success('Registered! Now complete your KYC.');
        setShowKYC(true);
      } catch (err) {
        toast.error(err.message);
      } finally { setCtaLoading(false); }
      return;
    }

    if (assignmentStatus === 'REGISTERED') {
      setShowKYC(true);
      return;
    }

    if (assignmentStatus === 'KYC_DONE') {
      setFlowStep('payment');
    }
  }, [user, assignmentStatus, linkToken]);

  // ─── KYC COMPLETE → update assignment ───────────────────────────────
  const handleKYCComplete = useCallback(async () => {
    setShowKYC(false);
    try {
      await fetch(`${API}/api/i/${linkToken}/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'KYC_DONE' }),
      });
      setAssignmentStatus('KYC_DONE');
      setFlowStep('payment');
      toast.success('KYC complete! You can now proceed to payment.');
    } catch {
      toast.error('Failed to update KYC status.');
    }
  }, [linkToken]);

  // ─── Payment complete handler (simulated for now) ──────────────────
  const handlePaymentComplete = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/i/${linkToken}/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          investorId: user?.userId || user?.id,
          paymentReference: `EOI_${Date.now()}`
        }),
      });
      if (res.ok) {
        setAssignmentStatus('EOI_PAID');
        setFlowStep('success');
        toast.success('EOI Payment confirmed!');
      }
    } catch {
      toast.error('Failed to confirm payment.');
    }
  }, [linkToken, user]);

  // ─── CTA label/color ────────────────────────────────────────────────
  let ctaLabel = 'Register to Proceed';
  let ctaColor = 'bg-[#4ADE80]';
  let ctaDisabled = false;
  if (!user) { ctaLabel = 'Sign Up / Login to Invest'; }
  else if (assignmentStatus === 'REGISTERED') { ctaLabel = 'Complete KYC'; ctaColor = 'bg-purple-500'; }
  else if (assignmentStatus === 'KYC_DONE') { ctaLabel = 'Proceed to Payment'; }
  else if (assignmentStatus === 'EOI_PAID' || assignmentStatus === 'CONVERTED') { ctaLabel = 'Investment Complete'; ctaDisabled = true; ctaColor = 'bg-white/10'; }

  // ─── FLOW STEP: SUCCESS ─────────────────────────────────────────────
  if (flowStep === 'success') {
    return <PaymentSuccessScreen assignment={assignment} unit={unit} user={user} linkToken={linkToken} navigate={navigate} />;
  }

  // ─── FLOW STEP: PAYMENT ─────────────────────────────────────────────
  if (flowStep === 'payment') {
    return <PaymentStep assignment={assignment} unit={unit} user={user} linkToken={linkToken} navigate={navigate} onPaymentComplete={handlePaymentComplete} />;
  }

  // ─── FLOW STEP: DETAILS (default landing) ───────────────────────────
  const steps = [
    { label: 'Register', done: !!user, active: !user },
    { label: 'KYC', done: ['KYC_DONE', 'EOI_PAID', 'CONVERTED'].includes(assignmentStatus), active: assignmentStatus === 'REGISTERED' },
    { label: 'EOI Payment', done: ['EOI_PAID', 'CONVERTED'].includes(assignmentStatus), active: assignmentStatus === 'KYC_DONE' },
    { label: 'Documents', done: false, active: false },
  ];

  return (
    <div className="min-h-screen bg-[#030303] text-white" data-testid="investment-landing">
      {/* Hero */}
      <div className="relative">
        <div className="h-48 bg-gradient-to-b from-[#4ADE80]/10 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="w-full max-w-2xl mx-auto px-4 pb-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-[#4ADE80]" />
                <span className="text-sm text-[#4ADE80] font-medium">{unit.projectName || 'Investment Opportunity'}</span>
              </div>
              <h1 className="text-3xl font-light tracking-tight">{unit.unitNumber || unit.unitName || 'Unit'}</h1>
              {unit.location && <div className="flex items-center gap-1 mt-2 text-white/40 text-sm"><MapPin className="w-3.5 h-3.5" /><span>{typeof unit.location === 'object' ? unit.location.fullAddress || `${unit.location.city}, ${unit.location.state}` : unit.location}</span></div>}
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Progress bar */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-1 px-2" data-testid="progress-steps">
          {steps.map((s, i) => (
            <div key={s.label} className="flex-1 flex items-center gap-1.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono shrink-0 ${
                s.done ? 'bg-[#4ADE80] text-black' : s.active ? 'border-2 border-[#4ADE80] text-[#4ADE80]' : 'border border-white/10 text-white/30'}`}>
                {s.done ? <Check className="w-3 h-3" /> : i + 1}
              </div>
              <span className={`text-[10px] ${s.done ? 'text-[#4ADE80]' : s.active ? 'text-white' : 'text-white/30'} hidden sm:block`}>{s.label}</span>
              {i < steps.length - 1 && <div className={`flex-1 h-px ${s.done ? 'bg-[#4ADE80]' : 'bg-white/10'}`} />}
            </div>
          ))}
        </motion.div>

        {/* Investment Card */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-5">
          <h2 className="text-sm text-white/40 uppercase tracking-wider mb-4">Your Investment</h2>
          <div className="space-y-4">
            <Row label="Investment Amount" value={<span className="text-2xl font-mono font-medium">{formatINR(assignment.assignedAmount)}</span>} />
            <Row label="Ownership" value={<span className="text-lg font-mono text-[#4ADE80]">{ownershipPct.toFixed(2)}%</span>} />
            <div className="h-px bg-white/5" />
            <Row label="Expression of Interest (EOI)" value={<span className="text-lg font-mono" data-testid="eoi-amount">{formatINR(EOI_AMOUNT)}</span>} />
            <Row label="Capital Payment (after documents)" value={<span className="text-sm font-mono text-white/40">{formatINR(capitalAmount)}</span>} />
          </div>
        </motion.div>

        {/* Broker */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-white/30" /></div>
          <div><p className="text-xs text-white/40">Referred by</p><p className="text-sm text-white">{broker.name || 'AltSlice Partner'}</p></div>
        </motion.div>

        {/* Expiry */}
        {hoursLeft !== null && !['EOI_PAID', 'CONVERTED'].includes(assignmentStatus) && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className={`flex items-center gap-2 px-4 py-3 rounded-sm text-sm ${hoursLeft < 12 ? 'bg-yellow-400/10 border border-yellow-400/20 text-yellow-400' : 'bg-white/5 border border-white/5 text-white/50'}`}>
            <Clock className="w-4 h-4" />
            <span>{hoursLeft > 0 ? `This invitation expires in ${hoursLeft} hours` : 'This invitation is about to expire'}</span>
          </motion.div>
        )}

        {/* CTA Button */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <Button onClick={handleCTA} disabled={ctaDisabled || ctaLoading}
            className={`w-full py-6 text-base font-medium ${ctaColor} text-black hover:opacity-90 disabled:opacity-40`}
            data-testid="cta-button">
            {ctaLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
            {ctaLabel}
            {!ctaDisabled && !ctaLoading && <ArrowRight className="w-5 h-5 ml-2" />}
          </Button>
          {!user && <p className="text-center text-xs text-white/30 mt-2">You need an account to invest</p>}
          <p className="text-center text-xs text-white/20 mt-3">Powered by AltSlice — Fractional Real Estate Investment Platform</p>
        </motion.div>

        {/* Trust badges */}
        <div className="flex items-center justify-center gap-6 pt-4">
          {[{ icon: Shield, text: 'SEBI Compliant' }, { icon: FileText, text: 'RERA Verified' }, { icon: CreditCard, text: 'Secure Payment' }].map(b => (
            <div key={b.text} className="flex items-center gap-1.5 text-white/30"><b.icon className="w-3.5 h-3.5" /><span className="text-xs">{b.text}</span></div>
          ))}
        </div>
      </div>

      {/* KYC Modal */}
      <KYCModal isOpen={showKYC} onClose={() => setShowKYC(false)} userId={user?.userId || user?.id} onComplete={handleKYCComplete} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* PAYMENT STEP                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

function PaymentStep({ assignment, unit, user, linkToken, navigate, onPaymentComplete }) {
  const [copied, setCopied] = useState('');
  const [confirming, setConfirming] = useState(false);
  const llp = unit.llp || {};
  const bank = llp.bankAccount || {};

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleConfirmPayment = async () => {
    setConfirming(true);
    try {
      await onPaymentComplete();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white" data-testid="payment-step">
      <div className="max-w-2xl mx-auto px-4 py-10 space-y-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
          <div className="w-16 h-16 bg-[#4ADE80]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-[#4ADE80]" />
          </div>
          <h2 className="text-2xl font-light mb-2">Registration & KYC Complete</h2>
          <p className="text-white/50">Transfer the EOI amount to complete your investment</p>
        </motion.div>

        {/* EOI amount highlight */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-[#4ADE80]/5 border border-[#4ADE80]/20 rounded-sm p-6 text-center">
          <p className="text-xs text-[#4ADE80] uppercase tracking-wider mb-2">EOI Amount Due</p>
          <p className="text-4xl font-mono font-medium text-[#4ADE80]" data-testid="eoi-payment-amount">{formatINR(EOI_AMOUNT)}</p>
          <p className="text-xs text-white/40 mt-2">Expression of Interest for {formatINR(assignment.assignedAmount)} investment</p>
        </motion.div>

        {/* Bank details */}
        {bank.accountNumber ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-[#0A0A0A] border border-white/5 rounded-sm p-5 space-y-4">
            <h3 className="text-sm text-white/40 uppercase tracking-wider">Transfer to LLP Account</h3>
            <BankRow label="Account Name" value={llp.name || 'LLP Account'} onCopy={() => copyText(llp.name, 'Name')} copied={copied === 'Name'} />
            <BankRow label="Account Number" value={bank.accountNumber} onCopy={() => copyText(bank.accountNumber, 'Account')} copied={copied === 'Account'} />
            <BankRow label="IFSC Code" value={bank.ifsc} onCopy={() => copyText(bank.ifsc, 'IFSC')} copied={copied === 'IFSC'} />
            <BankRow label="Bank" value={bank.bankName} />
            <BankRow label="Account Type" value={bank.accountType || 'Current'} />
            <div className="bg-yellow-400/5 border border-yellow-400/10 rounded-sm p-3 mt-2">
              <p className="text-xs text-yellow-400/80">
                Use your registered name as the sender. Reference: <strong className="font-mono">{assignment.assignmentId}</strong>
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6 text-center space-y-3">
            <IndianRupee className="w-8 h-8 text-white/20 mx-auto" />
            <p className="text-white/60">Payment details are being set up.</p>
            <p className="text-sm text-white/40">You'll receive an email and SMS with payment instructions within 24 hours.</p>
          </motion.div>
        )}

        {/* Confirm Payment Button (for testing / demo flow) */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}>
          <Button onClick={handleConfirmPayment} disabled={confirming}
            className="w-full py-5 bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-medium text-base"
            data-testid="confirm-payment-btn">
            {confirming ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <IndianRupee className="w-5 h-5 mr-2" />}
            I've Made the Payment
          </Button>
          <p className="text-center text-xs text-white/30 mt-2">Click after you have transferred {formatINR(EOI_AMOUNT)} to the account above</p>
        </motion.div>

        {/* What happens next */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-[#0A0A0A] border border-white/5 rounded-sm p-5">
          <h3 className="text-sm text-white/40 uppercase tracking-wider mb-4">What Happens Next</h3>
          <div className="space-y-3">
            {[
              { n: 1, text: `Transfer ${formatINR(EOI_AMOUNT)} EOI to the LLP account`, icon: IndianRupee },
              { n: 2, text: 'Partnership deed sent for e-signature via Zoho Sign', icon: FileText },
              { n: 3, text: `After signing, capital payment of ${formatINR((assignment.assignedAmount || 0) - EOI_AMOUNT)} will be requested`, icon: CreditCard },
              { n: 4, text: 'Once fully funded, you become an LLP partner', icon: Shield },
            ].map(s => (
              <div key={s.n} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center shrink-0 text-xs text-white/50 font-mono">{s.n}</div>
                <div className="flex items-center gap-2 text-sm text-white/70"><s.icon className="w-4 h-4 text-white/30 shrink-0" /><span>{s.text}</span></div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Support */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="text-center space-y-3 pt-4">
          <p className="text-xs text-white/30">Questions? Contact us</p>
          <div className="flex items-center justify-center gap-4">
            <a href="mailto:support@altslice.com" className="text-xs text-white/50 hover:text-white flex items-center gap-1"><Mail className="w-3.5 h-3.5" />support@altslice.com</a>
            <a href="tel:+919876543210" className="text-xs text-white/50 hover:text-white flex items-center gap-1"><Phone className="w-3.5 h-3.5" />+91 98765 43210</a>
          </div>
          <Button onClick={() => navigate('/investor')} variant="outline" className="mt-4 border-white/10 text-white/60 hover:bg-white/5" data-testid="go-dashboard-btn">
            Go to Dashboard <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* SMALL HELPERS                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */

function Row({ label, value }) {
  return <div className="flex justify-between items-baseline"><span className="text-sm text-white/50">{label}</span>{value}</div>;
}

function BankRow({ label, value, onCopy, copied }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <div><p className="text-xs text-white/40">{label}</p><p className="text-sm text-white font-mono">{value}</p></div>
      {onCopy && (
        <button onClick={onCopy} className="text-white/30 hover:text-white p-1" data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}>
          {copied ? <Check className="w-4 h-4 text-[#4ADE80]" /> : <Copy className="w-4 h-4" />}
        </button>
      )}
    </div>
  );
}
